import { useEffect, useMemo, useState } from 'react';
import { db } from '@/lib/db';
import type { CourseHole, PostEnriched, PostMetadataGroupScore } from '@/lib/db/types';
import { GroupScorecardGrid } from '@/components/play/GroupScorecardGrid';
import { holeRangeIndices } from '@/lib/scoring/hole-range';
import { personalizeRoundPostForMember } from '@/lib/profile/member-round-history';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  post: PostEnriched;
  viewerMemberId: string;
  onClose: () => void;
}

function buildSoloGroupScore(
  memberId: string,
  name: string,
  holeScores: (number | null)[],
): PostMetadataGroupScore[] {
  const scores = holeScores
    .map((strokes, i) => (strokes != null ? { hole_num: i + 1, strokes } : null))
    .filter((h): h is { hole_num: number; strokes: number } => h != null);
  if (!scores.length && holeScores.every((s) => s == null)) return [];
  return [{ id: memberId, name, scores }];
}

function scoresArrayFromGroup(g: PostMetadataGroupScore): (number | null)[] {
  const arr = Array(18).fill(null) as (number | null)[];
  (g.scores || []).forEach((h) => {
    if (h.hole_num >= 1 && h.hole_num <= 18) arr[h.hole_num - 1] = h.strokes;
  });
  return arr;
}

export function RoundSnapshotSheet({ post, viewerMemberId, onClose }: Props) {
  const viewPost = useMemo(
    () => personalizeRoundPostForMember(post, viewerMemberId),
    [post, viewerMemberId],
  );
  const round = viewPost.round;
  const metadataGroupScores = viewPost.metadata?.groupScores || [];

  const [fetchedHoles, setFetchedHoles] = useState<CourseHole[]>([]);

  useEffect(() => {
    if (!round?.course_id || round.course_holes?.length) {
      setFetchedHoles([]);
      return;
    }
    void db.courses.holes(round.course_id).then((holes) => {
      setFetchedHoles([...holes].sort((a, b) => a.hole_num - b.hole_num));
    });
  }, [round?.course_id, round?.course_holes?.length]);

  const sortedHoles = useMemo(() => {
    const base = round?.course_holes?.length ? round.course_holes : fetchedHoles;
    return [...(base || [])].sort((a, b) => a.hole_num - b.hole_num);
  }, [round?.course_holes, fetchedHoles]);

  const holeRange = round?.holes ?? 'full';
  const range = holeRangeIndices(holeRange);

  const holeScores = useMemo(() => {
    const arr = Array(18).fill(null) as (number | null)[];
    if (!round) return arr;
    (round.round_holes || []).forEach((h) => {
      if (h.hole_num >= 1 && h.hole_num <= 18 && h.strokes != null) {
        arr[h.hole_num - 1] = h.strokes;
      }
    });
    return arr;
  }, [round]);

  if (!round) {
    return (
      <Sheet title="Round" onClose={onClose}>
        <div className="p-4 text-sm text-mute">Round details unavailable.</div>
      </Sheet>
    );
  }

  const par = sortedHoles.length
    ? range.indices.reduce((sum, i) => sum + (sortedHoles[i]?.par ?? 4), 0)
    : (holeRange === 'full' ? 72 : 36);
  const isSelf = post.author_id === viewerMemberId;
  const groupPlayer = metadataGroupScores.find((g) => g.id === viewerMemberId);
  const playerName = isSelf
    ? (post.author.name || 'Player')
    : (groupPlayer?.name || 'Player');

  const recapScores = useMemo(() => {
    if (metadataGroupScores.length) return metadataGroupScores;
    return buildSoloGroupScore(viewerMemberId, playerName, holeScores);
  }, [metadataGroupScores, viewerMemberId, playerName, holeScores]);

  const gridPlayers = useMemo(
    () => recapScores.map((g) => ({
      id: g.id,
      name: g.name,
      guest: g.guest,
      you: g.id === viewerMemberId,
      scores: scoresArrayFromGroup(g),
    })),
    [recapScores, viewerMemberId],
  );

  const hasGrid = sortedHoles.length > 0 && gridPlayers.some((p) => p.scores.some((s) => s != null));

  return (
    <Sheet title="Round Scorecard" onClose={onClose}>
      <div className="space-y-3 px-4 pb-8 pt-2">
        <div className="rounded-xl border border-gold bg-gold/10 p-3">
          <div className="font-serif text-base font-bold text-navy">{round.course?.name || 'Course'}</div>
          <div className="text-xs text-mute">
            {round.tee?.name || '—'} tees · Par {par} · {new Date(round.played_at).toLocaleDateString()}
          </div>
          {!isSelf && (
            <div className="mt-1 text-[10px] text-mute">
              Posted by {post.author.name?.split(' ')[0] || 'group'}
            </div>
          )}
        </div>

        {hasGrid ? (
          <GroupScorecardGrid
            holes={sortedHoles}
            players={gridPlayers}
            range={range}
            highlightPlayerId={viewerMemberId}
          />
        ) : (
          <div className="rounded-xl border border-line bg-paper p-3 text-sm text-mute">
            Hole-by-hole scorecard is not available for this round.
          </div>
        )}
      </div>
    </Sheet>
  );
}
